import IconButton from "components/atoms/button/IconButton";
import Form from "components/atoms/form/Form";
import Icon from "components/atoms/icon/Icon";
import CheckIcon from "components/atoms/icon/icon-type/CheckIcon";
import LoadingScreen from "components/atoms/loading-screen/LoadingScreen";
import Table from "components/molecules/table/Table";
import { useMultiStepFormContext } from "lib/context/MultiStepFormContext/MultiStepFormContext";
import { IconType } from "lib/helpers/constants/iconTypes";
import { useUploadLeadStoreFile } from "lib/hooks/mutations/Leads/useUploadLeadStoreFile";
import { FC, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { Fragment } from "react/jsx-runtime";
import { createCampaignTableHeaders } from "lib/helpers/constants/columns/createCampaignTableHeaders";
import { useTranslation } from "react-i18next";
import Check4Icon from "components/atoms/icon/icon-type/Check4Icon";
export interface IStep4Props {
    setStep: any;
    dataCols: any;
    isAssignLeadsActive: string;
    onUpdateData: (step: number, updatedData: any[]) => void;
    themeColors: {
        primary: string;
    };
    setDataCols: any;
    assignTo?: {
        id: string;
        in_team?: string | null;
    };
    onConfirm: () => void;
}

interface FormData {
    data: any[];
    file: File | null;
    extra_fields: any[];

}

const Step4: FC<IStep4Props> = ({
    setStep,
    dataCols,
    assignTo,
    isAssignLeadsActive,
    onUpdateData,
    themeColors,
    setDataCols,
    onConfirm
}) => {
    const { handleSubmit } = useForm();
    const { formData }: { formData: FormData } = useMultiStepFormContext<any>();
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [_, setStoredLeads] = useState<string[]>();
    const { mutateAsync: storeUploadedLeads, isLoading } = useUploadLeadStoreFile();
    const [tableData, setTableData] = useState(formData.data || []);

    const [baseKeys, setBaseKeys] = useState<string[]>([]);
    const [extraFieldDefs, setExtraFieldDefs] = useState<any[]>([]);

    useEffect(() => {
        if (!formData?.data?.length) return;

        const toKey = (name: string) => name.toLowerCase().replace(/\s+/g, "_");

        // Collect base keys (original row keys except extra_fields)
        const base = Object.keys(formData.data[0]).filter(k => k !== "extra_fields");
        setBaseKeys(base);

        // Collect unique extra field definitions across all rows (or fall back to formData.extra_fields)
        const defsMap = new Map<string, any>(); // key => def
        formData.data.forEach((row: any) => {
            if (Array.isArray(row.extra_fields)) {
                row.extra_fields.forEach((f: any) => {
                    const k = toKey(f.field_name);
                    if (!defsMap.has(k)) defsMap.set(k, f);
                });
            }
        });

        // If no extra_fields found in rows, try form-level extra_fields if present
        if (defsMap.size === 0 && Array.isArray(formData.extra_fields)) {
            formData.extra_fields.forEach((f: any) => {
                const k = toKey(f.field_name);
                if (!defsMap.has(k)) defsMap.set(k, f);
            });
        }

        const defs = Array.from(defsMap.values());
        setExtraFieldDefs(defs);

        // Flatten into table rows for UI
        const mergedOnce = formData.data.map((row: any) => {
            const flat = { ...row };
            if (Array.isArray(row.extra_fields)) {
                row.extra_fields.forEach((f: any) => {
                    const k = toKey(f.field_name);
                    flat[k] = f.field_value ?? "";
                });
            }
            delete flat.extra_fields;
            return flat;
        });

        // Ensure every row has all extra field keys for consistent table columns
        const allExtraKeys = defs.map((f: any) => toKey(f.field_name));
        const merged = mergedOnce.map((row: any) => {
            allExtraKeys.forEach(k => {
                if (!(k in row)) row[k] = "";
            });
            return row;
        });

        setTableData(merged);
    }, [formData]);


    const handleRemove = (index: number) => {
        const newData = tableData.filter((_, i) => i !== index);
        setTableData(newData);
        setStoredLeads(newData);
        onUpdateData(1, newData);
    };


    // eslint-disable-next-line
    const actionsColumn = {
        Header: "Delete",
        accessor: "delete",
        Cell: ({ row }: any) => (
            <button
                type="button"
                onClick={() => handleRemove(row.index)}
                className="text-red-600 hover:text-red-800"
            >
                <Icon iconType={IconType.DELETE_ICON} color="#7D8592" />
            </button>
        ),
    };

    const dynamicHeaders = useMemo(() => {
        const defaultHeaders = createCampaignTableHeaders;
        const extras = (formData.extra_fields ?? []).map((field: any) => ({
            Header: field.field_name,
            accessor: field.field_name.toLowerCase().replace(/\s+/g, "_"),
            isChecked: true,
            Cell: ({ row }: any) =>
                row.original[field.field_name.toLowerCase().replace(/\s+/g, "_")] ?? field.field_value,
        }));
        return [actionsColumn, ...defaultHeaders, ...extras];
    }, [formData, actionsColumn]);

    const { t } = useTranslation();
    const onSubmit = () => {
        const toKey = (name: string) => name.toLowerCase().replace(/\s+/g, "_");

        // Reconstruct original rows (with extra_fields array)
        const leads = tableData.map((row: any) => {
            // base part
            const baseObj: any = {};
            baseKeys.forEach((k) => {
                // keep whatever is currently in the flattened row for base keys
                baseObj[k] = row[k];
            });

            // extra_fields rebuilt from definitions + row values
            const extra_fields = extraFieldDefs.map((def: any) => {
                const key = toKey(def.field_name);
                return {
                    section: def.section ?? "",
                    field_name: def.field_name,
                    field_value: row[key] ?? "",
                    field_type: def.field_type ?? "text",
                };
            });

            return { ...baseObj, extra_fields };
        });

        const payload =
            isAssignLeadsActive === "Own Leads"
                ? {
                    own_appointments: 1,
                    leads,
                    file: formData?.file,
                    extra_fields: formData?.extra_fields as any,
                    assign_to: assignTo || null,
                }
                : {
                    leads,
                    file: formData?.file,
                    extra_fields: formData?.extra_fields as any,
                    assign_to: assignTo || null,
                };

        storeUploadedLeads(payload)
            .then(() => {
                onConfirm();
                setStep(1);
                setStoredLeads([]);
            });
    };

    return (
        <Form
            onSubmit={handleSubmit(onSubmit)}
            className="mb-0 flex flex-col pl-4 pb-2"
        >
            <div className="border-l-[1.5px] border-dashed border-[#D8D8D8] pl-7 pb-9 relative bg-transparent">
                <div className="pl-2 text-[14px] font-inter-medium pb-5">
                    {t('Upload CSV')}
                </div>
                <div className="absolute top-[0] left-[-12px] z-9999">
                    <CheckIcon color={themeColors.primary} />
                </div>
            </div>
            <div className="border-l-[1.5px] border-dashed border-[#D8D8D8] pl-7 pb-9 relative bg-transparent">
                <div className="pl-2 text-[14px] font-inter-medium pb-5 ">
                    {t('Only adjust the fields you want to import')}
                </div>

                <div className="absolute top-[0] left-[-12px] z-9999 ">
                    <CheckIcon color={themeColors.primary} />
                </div>
            </div>
            <div className="border-l-[1.5px] border-dashed border-[#D8D8D8] pl-7 pb-9 relative bg-transparent">
                <div className="pl-2 text-[14px] font-inter-medium pb-5">
                    {t('Assign the leads to a worker')}
                </div>

                <div className="absolute top-[0] left-[-12px] z-9999 ">
                    <CheckIcon color={themeColors.primary} />
                </div>
            </div>
            <div className="pl-7 relative bg-transparent">
                <div className="pl-2 text-[14px] font-inter-medium pb-5 ">
                    {t("Remove the leads here that you don't want to import")}
                </div>

                <div
                    className="rounded-[20px] p-4 w-full "
                    style={{
                        backgroundColor: `${themeColors.primary}0D`,
                    }}
                >
                    <div className="overflow-auto horizontalOverflowScrollStyle w-full h-full px-5 ">
                        <Fragment>
                            <Table
                                style={{ backgroundColor: `${themeColors.primary}0D` }}
                                classes={{ subContainer: "!p-0 !rounded-none" }}
                                columns={dynamicHeaders}
                                data={tableData || []}
                                search={""}
                                isColumnSorted={true}
                            />
                        </Fragment>
                    </div>
                </div>

                <div className="absolute top-[0] left-[-12px] z-9999 ">
                    <Check4Icon color={themeColors.primary} />
                </div>
            </div>
            <div className="flex gap-5 justify-end pt-5">
                <IconButton
                    icon={<Icon iconType={IconType.UPLOAD_FILE_ICON} />}
                    className="w-full max-w-[150px]"
                    disabled={isLoading}
                >
                    {isLoading ? t("Creating...") : t("Continue")}
                </IconButton>
            </div>
            {isLoading && <LoadingScreen />}
        </Form>
    );
};

export default Step4;
